import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Save, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';
import { UpdatesSection } from './UpdatesSection';

const PRICE_FIELDS = [
  { key: 'price_print_simplex', label: 'Ч/Б печать одна сторона',   placeholder: '500'  },
  { key: 'price_print_duplex',  label: 'Ч/Б печать двухсторон',     placeholder: '1500' },
  { key: 'price_scan_simplex',  label: 'Сканирование одна сторона', placeholder: '500'  },
  { key: 'price_scan_duplex',   label: 'Сканирование двухсторон',   placeholder: '500'  },
];

export const Settings = () => {
  const { fetchAuthorized } = useAuth();
  const [prices, setPrices]     = useState({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [status, setStatus]     = useState(null);

  // Telemetry interval state
  const [telemetryInterval, setTelemetryInterval] = useState(10);
  const [savingTelemetry, setSavingTelemetry]       = useState(false);
  const [telemetryStatus, setTelemetryStatus]       = useState(null);

  // Reset state
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetting, setResetting]       = useState(false);
  const [resetStatus, setResetStatus]   = useState(null);

  const load = useCallback(async () => {
    try {
      const [priceRes, telRes] = await Promise.all([
        fetchAuthorized('/api/dashboard/settings/prices'),
        fetchAuthorized('/api/dashboard/settings/telemetry'),
      ]);
      const priceData = await priceRes.json();
      const telData   = await telRes.json();
      if (priceData.success) setPrices(priceData.prices);
      if (telData.success)   setTelemetryInterval(telData.telemetry_interval_minutes);
    } catch {}
    finally { setLoading(false); }
  }, [fetchAuthorized]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus(null);
    try {
      const res = await fetchAuthorized('/api/dashboard/settings/prices', {
        method: 'POST', body: JSON.stringify(prices),
      });
      setStatus(res.ok
        ? { ok: true,  text: 'Сохранено. Эти цены применятся к новым киоскам. Цены существующих киосков меняются в разделе «Киоски».' }
        : { ok: false, text: 'Ошибка при сохранении' }
      );
    } catch {
      setStatus({ ok: false, text: 'Ошибка соединения' });
    } finally {
      setSaving(false);
      setTimeout(() => setStatus(null), 5000);
    }
  };

  const saveTelemetry = async (e) => {
    e.preventDefault();
    setSavingTelemetry(true);
    setTelemetryStatus(null);
    try {
      const res = await fetchAuthorized('/api/dashboard/settings/telemetry', {
        method: 'POST',
        body: JSON.stringify({ telemetry_interval_minutes: telemetryInterval }),
      });
      const d = await res.json();
      setTelemetryStatus(res.ok && d.success
        ? { ok: true,  text: `Сохранено. Интервал ${d.telemetry_interval_minutes} мин отправлен всем киоскам.` }
        : { ok: false, text: d.error || 'Ошибка при сохранении' }
      );
    } catch {
      setTelemetryStatus({ ok: false, text: 'Ошибка соединения' });
    } finally {
      setSavingTelemetry(false);
      setTimeout(() => setTelemetryStatus(null), 5000);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    setResetStatus(null);
    try {
      const res  = await fetchAuthorized('/api/dashboard/orders/reset', {
        method: 'POST',
        body: JSON.stringify({ confirm: 'RESET' }),
      });
      const data = await res.json();
      if (data.success) {
        setResetStatus({ ok: true, text: `Удалено ${data.deleted} заказов. База чистая.` });
        setResetConfirm(false);
      } else {
        setResetStatus({ ok: false, text: data.error || 'Ошибка сброса' });
      }
    } catch {
      setResetStatus({ ok: false, text: 'Ошибка соединения' });
    } finally {
      setResetting(false);
      setTimeout(() => setResetStatus(null), 8000);
    }
  };

  // Transfer orders state
  const [devices, setDevices]               = useState([]);
  const [transferFrom, setTransferFrom]     = useState('');
  const [transferTo, setTransferTo]         = useState('');
  const [transferConfirm, setTransferConfirm] = useState(false);
  const [transferring, setTransferring]     = useState(false);
  const [transferStatus, setTransferStatus] = useState(null);

  const deviceLabel = (id) => {
    const d = devices.find(x => x.device_id === id);
    return d ? (d.name || d.machine_name || d.device_id.slice(0, 14)) : '';
  };

  const handleTransfer = async () => {
    setTransferring(true);
    setTransferStatus(null);
    try {
      const res  = await fetchAuthorized('/api/dashboard/orders/transfer', {
        method: 'POST',
        body: JSON.stringify({ from_device_id: transferFrom, to_device_id: transferTo, confirm: 'TRANSFER' }),
      });
      const data = await res.json();
      if (data.success) {
        setTransferStatus({ ok: true, text:
          `Готово. ${deviceLabel(transferFrom)} → ${deviceLabel(transferTo)}: ${data.moved}; ` +
          `${deviceLabel(transferTo)} → ${deviceLabel(transferFrom)}: ${data.moved_back}.` });
        setTransferConfirm(false);
      } else {
        setTransferStatus({ ok: false, text: data.error || 'Ошибка переноса' });
      }
    } catch {
      setTransferStatus({ ok: false, text: 'Ошибка соединения' });
    } finally {
      setTransferring(false);
      setTimeout(() => setTransferStatus(null), 10000);
    }
  };

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetchAuthorized('/api/dashboard/devices')
      .then(r => r.json())
      .then(d => { if (d.success) setDevices(d.devices); })
      .catch(() => {});
  }, [fetchAuthorized]);

  if (loading) {
    return (
      <div className="fade-in">
        <div className="page-header"><div><h1>Настройки</h1><p>Цены и параметры сети</p></div></div>
        <p className="text-dim" style={{ fontSize: '13px' }}>Загрузка...</p>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1>Настройки</h1>
          <p>Цены и параметры сети</p>
        </div>
      </div>

      {/* Prices */}
      <div className="card" style={{ maxWidth: '800px', padding: '28px', marginBottom: '20px' }}>
        <p className="section-title" style={{ marginBottom: '6px' }}>Цены по умолчанию (UZS за 1 единицу)</p>
        <p className="text-dim" style={{ fontSize: '12px', marginBottom: '18px' }}>
          Применяются только к новым киоскам при создании. У каждого киоска свои цены —
          они настраиваются индивидуально в разделе «Киоски» (кнопка «Цены»).
        </p>

        <form onSubmit={save}>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '20px' }}>
            {PRICE_FIELDS.map(({ key, label, placeholder }) => (
              <div key={key} className="field" style={{ flex: '1 1 180px' }}>
                <label className="field-label">{label}</label>
                <div className="field-wrap">
                  <input
                    type="number"
                    min="0"
                    value={prices[key] || ''}
                    onChange={e => setPrices({ ...prices, [key]: e.target.value })}
                    placeholder={placeholder}
                    style={{ paddingRight: '44px' }}
                  />
                  <span className="field-suffix">UZS</span>
                </div>
              </div>
            ))}
          </div>

          {status && (
            <div className={`alert ${status.ok ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '14px' }}>
              {status.ok
                ? <CheckCircle size={14} style={{ flexShrink: 0 }} />
                : <AlertCircle size={14} style={{ flexShrink: 0 }} />
              }
              <span>{status.text}</span>
            </div>
          )}

          <button type="submit" disabled={saving} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '10px' }}>
            <Save size={14} />
            {saving ? 'Сохранение...' : 'Сохранить цены'}
          </button>
        </form>
      </div>

      {/* Software updates */}
      <UpdatesSection />

      {/* Telemetry interval */}
      <div className="card" style={{ maxWidth: '800px', padding: '28px', marginBottom: '20px' }}>
        <p className="section-title" style={{ marginBottom: '6px' }}>Интервал телеметрии</p>
        <p className="text-dim" style={{ fontSize: '12px', marginBottom: '18px' }}>
          Как часто каждый киоск отправляет статус принтера и сканера. Бумага отправляется мгновенно при изменении.
          Диапазон: 1–60 минут.
        </p>
        <form onSubmit={saveTelemetry}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '16px' }}>
            <div className="field" style={{ flex: '0 0 180px' }}>
              <label className="field-label">Интервал</label>
              <div className="field-wrap">
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={telemetryInterval}
                  onChange={e => setTelemetryInterval(Math.max(1, Math.min(60, parseInt(e.target.value) || 1)))}
                  style={{ paddingRight: '44px' }}
                />
                <span className="field-suffix">мин</span>
              </div>
            </div>
          </div>

          {telemetryStatus && (
            <div className={`alert ${telemetryStatus.ok ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '14px' }}>
              {telemetryStatus.ok
                ? <CheckCircle size={14} style={{ flexShrink: 0 }} />
                : <AlertCircle size={14} style={{ flexShrink: 0 }} />
              }
              <span>{telemetryStatus.text}</span>
            </div>
          )}

          <button type="submit" disabled={savingTelemetry} className="btn btn-primary" style={{ justifyContent: 'center', padding: '10px 24px' }}>
            <Save size={14} />
            {savingTelemetry ? 'Сохранение...' : 'Сохранить и разослать киоскам'}
          </button>
        </form>
      </div>

      {/* Transfer order history between kiosks */}
      <div className="card" style={{ maxWidth: '800px', padding: '28px', marginBottom: '20px', borderColor: 'rgba(239,68,68,0.25)' }}>
        <p className="section-title" style={{ marginBottom: '6px' }}>Перенос истории заказов</p>
        <p className="text-dim" style={{ fontSize: '12px', marginBottom: '18px' }}>
          Меняет киоски заказами местами: заказы A становятся заказами B, а заказы B — заказами A
          (вместе с выручкой в статистике). Ничего не удаляется; повторный перенос возвращает всё как было.
          Профили, цены и сами устройства не меняются. Заказы, которые прямо сейчас в процессе оплаты/печати, не трогаются.
        </p>

        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {[
            { label: 'Откуда (A)', value: transferFrom, set: setTransferFrom },
            { label: 'Куда (B)',   value: transferTo,   set: setTransferTo   },
          ].map(({ label, value, set }) => (
            <div key={label} className="field" style={{ flex: '1 1 220px' }}>
              <label className="field-label">{label}</label>
              <select
                value={value}
                onChange={e => { set(e.target.value); setTransferConfirm(false); }}
                style={{ width: '100%', height: '38px', fontSize: '13px', borderRadius: '8px', padding: '0 10px', background: 'var(--bg-card)', color: 'var(--text-1)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
              >
                <option value="">— выберите киоск —</option>
                {devices.map(d => (
                  <option key={d.device_id} value={d.device_id}>
                    {d.name || d.machine_name || d.device_id.slice(0, 14)}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>

        {transferStatus && (
          <div className={`alert ${transferStatus.ok ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '14px' }}>
            {transferStatus.ok
              ? <CheckCircle size={14} style={{ flexShrink: 0 }} />
              : <AlertCircle size={14} style={{ flexShrink: 0 }} />
            }
            <span>{transferStatus.text}</span>
          </div>
        )}

        {!transferConfirm ? (
          <button
            className="btn btn-primary"
            disabled={!transferFrom || !transferTo || transferFrom === transferTo}
            onClick={() => setTransferConfirm(true)}
          >
            Перенести заказы
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', color: 'var(--red)', fontWeight: '600' }}>
              {`Поменять местами заказы «${deviceLabel(transferFrom)}» и «${deviceLabel(transferTo)}»?`}
            </span>
            <button className="btn btn-danger" onClick={handleTransfer} disabled={transferring}>
              {transferring ? 'Переносим...' : 'Да, выполнить'}
            </button>
            <button className="btn" onClick={() => setTransferConfirm(false)}>
              Отмена
            </button>
          </div>
        )}
      </div>

      {/* Danger zone — reset test data */}
      <div className="card" style={{ maxWidth: '800px', padding: '28px', borderColor: 'rgba(239,68,68,0.25)' }}>
        <p style={{ fontSize: '13px', fontWeight: '700', color: 'var(--red)', marginBottom: '8px' }}>
          Опасная зона
        </p>
        <p className="text-dim" style={{ fontSize: '12px', marginBottom: '16px' }}>
          Удалить все заказы из базы данных. Используется для очистки тестовых данных.
          Устройства и настройки цен не затрагиваются.
        </p>

        {resetStatus && (
          <div className={`alert ${resetStatus.ok ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '14px' }}>
            {resetStatus.ok
              ? <CheckCircle size={14} style={{ flexShrink: 0 }} />
              : <AlertCircle size={14} style={{ flexShrink: 0 }} />
            }
            <span>{resetStatus.text}</span>
          </div>
        )}

        {!resetConfirm ? (
          <button
            className="btn btn-danger"
            style={{ gap: '6px' }}
            onClick={() => setResetConfirm(true)}
          >
            <Trash2 size={13} /> Очистить все заказы
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', color: 'var(--red)', fontWeight: '600' }}>
              Точно удалить все заказы?
            </span>
            <button
              className="btn btn-danger"
              onClick={handleReset}
              disabled={resetting}
            >
              {resetting ? 'Удаляем...' : 'Да, удалить'}
            </button>
            <button className="btn" onClick={() => setResetConfirm(false)}>
              Отмена
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
